import { __extends } from "tslib";
import { ASObject } from '@awayfl/avm2';
import { Point as AwayPoint } from '@awayjs/core';
var Point = /** @class */ (function (_super) {
    __extends(Point, _super);
    /**
   * Creates a new point. If you pass no parameters to this method, a point is
   * created at(0,0).
   *
   * @param x The horizontal coordinate.
   * @param y The vertical coordinate.
   */
    function Point(xAdaptee, y) {
        if (xAdaptee === void 0) { xAdaptee = 0; }
        if (y === void 0) { y = 0; }
        var _this = _super.call(this) || this;
        _this._constructorCalled = true;
        _this._adaptee = (xAdaptee instanceof AwayPoint) ? xAdaptee : new AwayPoint(+xAdaptee, +y);
        _this._constructorCalled = true;
        return _this;
    }
    Object.defineProperty(Point.prototype, "adaptee", {
        get: function () {
            return this._adaptee;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Point.prototype, "x", {
        /**
       * The horizontal coordinate of the point. The default value is 0.
       */
        get: function () {
            if (!this._constructorCalled) {
                console.warn('[Point] Read x before call constructor');
                return 0;
            }
            return this._adaptee._rawData[0];
        },
        set: function (value) {
            this._adaptee._rawData[0] = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Point.prototype, "y", {
        /**
       * The vertical coordinate of the point. The default value is 0.
       */
        get: function () {
            if (!this._constructorCalled) {
                console.warn('[Point] Read y before call constructor');
                return 0;
            }
            return this._adaptee._rawData[1];
        },
        set: function (value) {
            this._adaptee._rawData[1] = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Point.prototype, "length", {
        /**
       * The length of the line segment from(0,0) to this point.
       */
        get: function () {
            return this._adaptee.length;
        },
        enumerable: true,
        configurable: true
    });
    /**
   * Determines a point between two specified points. The parameter
   * <code>f</code> determines where the new interpolated point is located
   * relative to the two end points specified by parameters <code>pt1</code>
   * and <code>pt2</code>. The closer the value of the parameter <code>f</code>
   * is to <code>1.0</code>, the closer the interpolated point is to the first
   * point(parameter <code>pt1</code>). The closer the value of the parameter
   * <code>f</code> is to 0, the closer the interpolated point is to the second
   * point(parameter <code>pt2</code>).
   *
   * @param pt1 The first point.
   * @param pt2 The second point.
   * @param f   The level of interpolation between the two points. Indicates
   *            where the new point will be, along the line between
   *            <code>pt1</code> and <code>pt2</code>. If <code>f</code>=1,
   *            <code>pt1</code> is returned; if <code>f</code>=0,
   *            <code>pt2</code> is returned.
   * @return The new, interpolated point.
   */
    Point.interpolate = function (pt1, pt2, f) {
        return new this.sec.flash.geom.Point(AwayPoint.interpolate(pt1.adaptee, pt2.adaptee, f));
    };
    /**
   * Returns the distance between <code>pt1</code> and <code>pt2</code>.
   *
   * @param pt1 The first point.
   * @param pt2 The second point.
   * @return The distance between the first and second points.
   */
    Point.distance = function (pt1, pt2) {
        return AwayPoint.distance(pt1.adaptee, pt2.adaptee);
    };
    /**
   * Converts a pair of polar coordinates to a Cartesian point coordinate.
   *
   * @param len   The length coordinate of the polar pair.
   * @param angle The angle, in radians, of the polar pair.
   * @return The Cartesian point.
   */
    Point.polar = function (len, angle) {
        return new this.sec.flash.geom.Point(AwayPoint.polar(+len, +angle));
    };
    /**
   * Creates a copy of this Point object.
   *
   * @return The new Point object.
   */
    Point.prototype.clone = function () {
        return new this.sec.flash.geom.Point(this._adaptee.clone());
    };
    /**
   * Offsets the Point object by the specified amount. The value of
   * <code>dx</code> is added to the original value of <i>x</i> to create the
   * new <i>x</i> value. The value of <code>dy</code> is added to the original
   * value of <i>y</i> to create the new <i>y</i> value.
   *
   * @param dx The amount by which to offset the horizontal coordinate,
   *           <i>x</i>.
   * @param dy The amount by which to offset the vertical coordinate, <i>y</i>.
   */
    Point.prototype.offset = function (dx, dy) {
        this._adaptee.offset(dx, dy);
    };
    /**
   * Determines whether two points are equal. Two points are equal if they have
   * the same <i>x</i> and <i>y</i> values.
   *
   * @param toCompare The point to be compared.
   * @return A value of <code>true</code> if the object is equal to this Point
   *         object; <code>false</code> if it is not equal.
   */
    Point.prototype.equals = function (toCompare) {
        return this._adaptee.equals(toCompare.adaptee);
    };
    /**
   * Subtracts the coordinates of another point from the coordinates of this
   * point to create a new point.
   *
   * @param v The point to be subtracted.
   * @return The new point.
   */
    Point.prototype.subtract = function (v) {
        return new this.sec.flash.geom.Point(this._adaptee.subtract(v.adaptee));
    };
    /**
   * Adds the coordinates of another point to the coordinates of this point to
   * create a new point.
   *
   * @param v The point to be added.
   * @return The new point.
   */
    Point.prototype.add = function (v) {
        return new this.sec.flash.geom.Point(this._adaptee.add(v.adaptee));
    };
    /**
   * Scales the line segment between(0,0) and the current point to a set
   * length.
   *
   * @param thickness The scaling value. For example, if the current point is
   *                 (0,5), and you normalize it to 1, the point returned is
   *                  at(0,1).
   */
    Point.prototype.normalize = function (thickness) {
        if (thickness === void 0) { thickness = 1; }
        this._adaptee.normalize(+thickness);
    };
    Point.prototype.copyFrom = function (sourcePoint) {
        this._adaptee.copyFrom(sourcePoint.adaptee);
    };
    Point.prototype.setTo = function (x, y) {
        this._adaptee.setTo(x, y);
    };
    Point.prototype.toString = function () {
        return '(x=' + this.x + ', y=' + this.y + ')';
    };
    // Called whenever the class is initialized.
    Point.classInitializer = null;
    // List of static symbols to link.
    Point.classSymbols = null; // ["interpolate", "distance", "polar"];
    // List of instance symbols to link.
    Point.instanceSymbols = null;
    return Point;
}(ASObject));
export { Point };
